import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  CreateTaskNote,
  CreateTaskRequest,
  MoveTaskRequest,
  Task,
  TaskNote,
  TaskPosition,
  UpdateDoneAtRequest,
  UpdatePupilDoneRequest,
  UpdateReflectionRequest,
  UpdateTaskRequest,
} from '@lyceo/shared/plan/util-models';
import { addDays, addWeeks, format } from 'date-fns';
import { map, Observable } from 'rxjs';
import { PLAN_CONFIG, PlanConfig } from '@lyceo/shared/plan/util-provider';

@Injectable({
  providedIn: 'root',
})
export class TaskService {
  private readonly planConfig: PlanConfig = inject<PlanConfig>(PLAN_CONFIG);
  private readonly http: HttpClient = inject(HttpClient);

  /**
   * Get all the tasks that are not, and have no, subtasks, that still need to
   * be planned.
   */
  public getTodoTasks(pupilId: string): Observable<Task[]> {
    const today: Date = new Date();
    const url: string = `${this.planConfig.homiApiUrl}/api/pupils/${pupilId}/tasks`;
    const headersToSend: HttpHeaders = new HttpHeaders().set(
      'Accept',
      'application/json',
    );
    const paramsToSend: HttpParams = new HttpParams()
      .set('periodStart', format(today, 'yyyy-MM-dd'))
      .set('periodEnd', format(addWeeks(today, 6), 'yyyy-MM-dd'))
      .set('hideDone', 'true')
      .set('hasParent', 'false')
      .set('hasSubtasks', 'false');

    return this.http.get<Task[]>(url, {
      headers: headersToSend,
      params: paramsToSend,
    });
  }

  public fetchByDate(pupilId: string, date: Date): Observable<Task[]> {
    const formattedScheduledAt: string = format(date, 'yyyy-MM-dd');
    const url: string = `${this.planConfig.homiApiUrl}/api/pupils/${pupilId}/tasks`;
    const headersToSend: HttpHeaders = new HttpHeaders().set(
      'Accept',
      'application/json',
    );
    const paramsToSend: HttpParams = new HttpParams().set(
      'scheduledAt',
      formattedScheduledAt,
    );

    return this.http.get<Task[]>(url, {
      headers: headersToSend,
      params: paramsToSend,
    });
  }

  public fetchByRange(
    pupilId: string,
    periodStart: Date,
    periodEnd: Date,
  ): Observable<Task[]> {
    const formattedPeriodStart: string = format(periodStart, 'yyyy-MM-dd');
    const formattedPeriodEnd: string = format(
      addDays(periodEnd, 1),
      'yyyy-MM-dd',
    );
    const url: string = `${this.planConfig.homiApiUrl}/api/pupils/${pupilId}/tasks`;
    const headersToSend: HttpHeaders = new HttpHeaders().set(
      'Accept',
      'application/json',
    );
    const paramsToSend: HttpParams = new HttpParams()
      .set('periodStart', formattedPeriodStart)
      .set('periodEnd', formattedPeriodEnd);

    return this.http.get<Task[]>(url, {
      headers: headersToSend,
      params: paramsToSend,
    });
  }

  public create(pupilId: string, request: CreateTaskRequest): Observable<Task> {
    const url: string = `${this.planConfig.homiApiUrl}/api/pupils/${pupilId}/tasks`;
    const headersToSend: HttpHeaders = new HttpHeaders().set(
      'Accept',
      'application/json',
    );
    return this.http.post<Task>(url, request, {
      headers: headersToSend,
    });
  }

  public update(pupilId: string, request: UpdateTaskRequest): Observable<Task> {
    const url: string = `${this.planConfig.homiApiUrl}/api/pupils/${pupilId}/tasks/${request.id}`;
    const headersToSend: HttpHeaders = new HttpHeaders()
      .set('Accept', 'application/json')
      .set('Content-Type', 'application/merge-patch+json');
    return this.http.patch<Task>(url, request, {
      headers: headersToSend,
    });
  }

  public move(
    pupilId: string,
    request: MoveTaskRequest,
  ): Observable<TaskPosition[]> {
    const url: string = `${this.planConfig.homiApiUrl}/api/pupils/${pupilId}/tasks/${request.id}/move`;
    const headersToSend: HttpHeaders = new HttpHeaders()
      .set('Accept', 'application/json')
      .set('Content-Type', 'application/merge-patch+json');
    return this.http
      .patch<{ tasks: TaskPosition[] }>(url, request, {
        headers: headersToSend,
      })
      .pipe(map((response) => response.tasks));
  }

  public updatePupilDone(
    pupilId: string,
    request: UpdatePupilDoneRequest,
  ): Observable<Task> {
    const url: string = `${this.planConfig.homiApiUrl}/api/pupils/${pupilId}/tasks/${request.id}`;
    const headersToSend: HttpHeaders = new HttpHeaders()
      .set('Accept', 'application/json')
      .set('Content-Type', 'application/merge-patch+json');
    return this.http.patch<Task>(url, request, {
      headers: headersToSend,
    });
  }

  public updateReflection(
    pupilId: string,
    request: UpdateReflectionRequest,
  ): Observable<Task> {
    const url: string = `${this.planConfig.homiApiUrl}/api/pupils/${pupilId}/tasks/${request.id}`;
    const headersToSend: HttpHeaders = new HttpHeaders()
      .set('Accept', 'application/json')
      .set('Content-Type', 'application/merge-patch+json');
    return this.http.patch<Task>(url, request, {
      headers: headersToSend,
    });
  }

  public updateDoneAt(
    pupilId: string,
    request: UpdateDoneAtRequest,
  ): Observable<Task> {
    if (!request.done) {
      request.pupilDone = false;
    }
    const url: string = `${this.planConfig.homiApiUrl}/api/pupils/${pupilId}/tasks/${request.id}`;
    const headersToSend: HttpHeaders = new HttpHeaders()
      .set('Accept', 'application/json')
      .set('Content-Type', 'application/merge-patch+json');
    return this.http.patch<Task>(url, request, {
      headers: headersToSend,
    });
  }

  public get(pupilId: string, id: string, full?: boolean): Observable<Task> {
    const url: string = `${this.planConfig.homiApiUrl}/api/pupils/${pupilId}/tasks/${id}`;

    const headersToSend: HttpHeaders = new HttpHeaders().set(
      'Accept',
      'application/json',
    );

    let paramsToSend: HttpParams | undefined;
    if (full) {
      paramsToSend = new HttpParams().set('group[]', 'full');
    }

    return this.http.get<Task>(url, {
      headers: headersToSend,
      params: paramsToSend,
    });
  }

  public delete(pupilId: string, id: string): Observable<void> {
    const url: string = `${this.planConfig.homiApiUrl}/api/pupils/${pupilId}/tasks/${id}`;
    const headersToSend: HttpHeaders = new HttpHeaders().set(
      'Accept',
      'application/json',
    );
    return this.http.delete<void>(url, {
      headers: headersToSend,
    });
  }

  public upsertNote(note: CreateTaskNote | TaskNote): Observable<TaskNote> {
    if (!(note as TaskNote).id) {
      return this.createNote(note as CreateTaskNote);
    }

    return this.updateNote(note as TaskNote);
  }

  public createNote(note: CreateTaskNote): Observable<TaskNote> {
    const url: string = `${this.planConfig.homiApiUrl}/api/task-notes`;
    const headersToSend: HttpHeaders = new HttpHeaders().set(
      'Accept',
      'application/json',
    );
    return this.http.post<TaskNote>(url, note, {
      headers: headersToSend,
    });
  }

  public updateNote(note: TaskNote): Observable<TaskNote> {
    const url: string = `${this.planConfig.homiApiUrl}/api/task-notes/${note.id}`;
    const headersToSend: HttpHeaders = new HttpHeaders()
      .set('Accept', 'application/json')
      .set('Content-Type', 'application/merge-patch+json');
    return this.http.patch<TaskNote>(url, note, {
      headers: headersToSend,
    });
  }
}
